import Button from '@app-studio-web/ui/components/Button'
import Layout from '@app-studio-web/ui/components/Layout'
import * as Sentry from '@sentry/react'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, useSubmit } from 'react-router-dom'

type CheckboxOption = {
  text: string
  value: string
  icon: string
}

export default function QuestionPastAttemptsPage() {
  const { t } = useTranslation()
  const submit = useSubmit()
  const [selectedValues, setSelectedValues] = useState<string[]>([])

  const heading = t('pages.onboarding.pastAttempts.heading')
  const subheading = t('pages.onboarding.pastAttempts.subheading')
  const options: CheckboxOption[] = useMemo(
    () => [
      {
        text: t('pages.onboarding.pastAttempts.option1'),
        value: 'meditation',
        icon: '🧘',
      },
      {
        text: t('pages.onboarding.pastAttempts.option2'),
        value: 'journaling',
        icon: '📝',
      },
      {
        text: t('pages.onboarding.pastAttempts.option3'),
        value: 'therapy',
        icon: '💬',
      },
      {
        text: t('pages.onboarding.pastAttempts.option4'),
        value: 'exercise',
        icon: '🏃',
      },
      {
        text: t('pages.onboarding.pastAttempts.option5'),
        value: 'apps',
        icon: '📱',
      },
      {
        text: t('pages.onboarding.pastAttempts.option6'),
        value: 'nothing',
        icon: '🆕',
      },
    ],
    [t],
  )

  const toggleOption = (value: string) => {
    if (value === 'nothing') {
      setSelectedValues(['nothing'])
    } else {
      setSelectedValues((prev) => {
        const filtered = prev.filter((v) => v !== 'nothing')
        if (filtered.includes(value)) {
          return filtered.filter((v) => v !== value)
        }
        return [...filtered, value]
      })
    }
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const hiddenInput = document.createElement('input')
    hiddenInput.type = 'hidden'
    hiddenInput.name = 'answer'
    hiddenInput.value = selectedValues.join(',')
    event.currentTarget.appendChild(hiddenInput)

    try {
      sessionStorage.setItem(
        'onboarding_past_attempts',
        selectedValues.join(','),
      )
    } catch (error) {
      Sentry.captureException(error)
    }

    submit(event.currentTarget)
    event.currentTarget.removeChild(hiddenInput)
  }

  return (
    <Layout>
      <div className="fixed inset-0 -z-10 bg-gradient-to-b from-[#FFFFFF] to-[#EFFBFF]" />
      <div className="flex h-full flex-col">
        <div className="mt-11 shrink-0 text-center">
          <h1 className="custom-heading mb-2" data-testid="label-header">
            {heading}
          </h1>
          <p className="text-gray-600">{subheading}</p>
        </div>

        <div className="flex grow flex-col items-center justify-center pb-6">
          <div className="grid w-full max-w-md grid-cols-2 gap-3">
            {options.map((option, index) => (
              <button
                key={`option-${index}`}
                type="button"
                className={`flex cursor-pointer flex-col items-center gap-2 rounded-[20px] border bg-white p-4 text-center transition-all hover:shadow-md ${
                  selectedValues.includes(option.value)
                    ? 'border-2 border-blue-400 bg-blue-50'
                    : 'border-gray-200'
                }`}
                data-testid={`button-option-${index + 1}`}
                onClick={() => toggleOption(option.value)}
              >
                <span className="text-3xl">{option.icon}</span>
                <span className="text-sm font-medium">{option.text}</span>
              </button>
            ))}
          </div>
        </div>

        <Form className="shrink-0" method="post" onSubmit={handleSubmit}>
          <Button
            title={t('shared.continue')}
            type="submit"
            dataTestId="button-continue"
            disabled={selectedValues.length === 0}
          />
        </Form>
      </div>
    </Layout>
  )
}
